'use client';

import { bunja } from 'bunja';
import { createScopeFromContext, useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { PropsWithChildren, ReactNode, createContext, memo, useCallback, useEffect, useId, useMemo } from 'react';

import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import deferred, { Deferred } from '@flipster/utils/deferred';
import { generateID } from '@flipster/utils/identity';

export interface OverlayControllerProps {
  overlayId: string;
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
}

export type OverlayProps = OverlayControllerProps;

export type OverlayAsyncControllerProps<T> = Omit<OverlayControllerProps, 'close'> & {
  close: (param: T) => void;
  reject: (reason?: unknown) => void;
};

export interface OverlayOpenOptions {
  overlayId?: string;
  /** Groups overlays so `closeSlot` dismisses that family only. Default `modal`. */
  slot?: string;
}

export interface OverlayRenderer {
  (props: OverlayControllerProps): ReactNode;
}

export interface OverlayAsyncRenderer<T> {
  (props: OverlayAsyncControllerProps<T>): ReactNode;
}

export function useOverlay() {
  const { open, openAsync, close, unmount, closeAll, unmountAll, closeSlot, getClosed } = useBunja(overlayBunja);
  const overlay = useMemo(
    () => ({ open, openAsync, close, unmount, closeAll, unmountAll, closeSlot, getClosed }),
    [open, openAsync, close, unmount, closeAll, unmountAll, closeSlot, getClosed],
  );
  return overlay;
}

export function useLocalOverlay() {
  const localId = useId();
  const { open, openAsync, close, unmount, closeAll, unmountAll, closeSlot, getClosed, presenceItemsAtom } = useBunja(
    overlayBunja,
    [OverlayIdScope.bind(localId)],
  );
  const presenceItems = useAtomValue(presenceItemsAtom);
  const placeholder = useMemo(
    () => (
      <OverlayIdContext value={localId}>
        <OverlayAnnouncer items={presenceItems} />
      </OverlayIdContext>
    ),
    [localId, presenceItems],
  );
  const overlay = useMemo(
    () => ({ open, openAsync, close, unmount, closeAll, unmountAll, closeSlot, getClosed, placeholder }),
    [open, openAsync, close, unmount, closeAll, unmountAll, closeSlot, getClosed, placeholder],
  );
  return overlay;
}

export function useOverlayItem() {
  return useBunja(overlayItemBunja);
}

export interface OverlayRendererProps {
  overlayId: string;
  isOpen: boolean;
  renderer: OverlayRenderer;
}

const OverlayRenderingProvider = memo(({ overlayId, isOpen, renderer: Renderer }: OverlayRendererProps) => {
  const overlayBunjaInstance = useBunja(overlayBunja);

  const close = useCallback(() => {
    overlayBunjaInstance.close(overlayId);
  }, [overlayBunjaInstance, overlayId]);

  const unmount = useCallback(() => {
    overlayBunjaInstance.unmount(overlayId);
  }, [overlayBunjaInstance, overlayId]);

  useEffect(() => {
    requestAnimationFrame(() => {
      overlayBunjaInstance.initialize(overlayId);
    });
  }, [overlayBunjaInstance, overlayId]);

  return <Renderer overlayId={overlayId} isOpen={isOpen} close={close} unmount={unmount} />;
});

const OverlayAnnouncer = ({ items }: { items: OverlayItem[] }) => {
  return (
    <>
      {items.map((item) => (
        <OverlayItemContext key={item.instanceKey} value={item}>
          <OverlayRenderingProvider overlayId={item.id} isOpen={item.isOpen} renderer={item.renderer} />
        </OverlayItemContext>
      ))}
    </>
  );
};

export const OverlayProvider = ({ children }: PropsWithChildren) => {
  const { presenceItemsAtom } = useBunja(overlayBunja);
  const presenceItems = useAtomValue(presenceItemsAtom);
  return (
    <>
      {children}
      <OverlayAnnouncer items={presenceItems} />
    </>
  );
};

const OverlayIdContext = createContext<string>('');
const OverlayIdScope = createScopeFromContext(OverlayIdContext);

export const overlayBunja = bunja(() => {
  bunja.use(OverlayIdScope);
  const store = bunja.use(JotaiStoreScope);
  const closedTable: Record<string, Deferred<void>> = {};

  const slotsAtom = atom<Record<string, OverlayItem>>({});
  const presenceItemsAtom = atom<OverlayItem[]>((get) => {
    const slots = get(slotsAtom);
    return Object.values(slots).filter(Boolean);
  });

  function open(renderer: OverlayRenderer, options?: OverlayOpenOptions) {
    const slot = options?.slot ?? 'modal';
    const id = options?.overlayId ?? generateID('overlay-');
    const existing = store.get(slotsAtom)[id];
    if (existing?.isOpen) {
      throw new Error(`You can't open multiple overlays with the same overlayId (${id}). Please set a different id.`);
    }

    const closed = deferred<void>();
    closedTable[id] = closed;
    store.set(slotsAtom, (prev) => ({
      ...prev,
      [id]: {
        id,
        instanceKey: generateID('overlay-'),
        slot,
        renderer,
        isOpen: false,
      },
    }));
    return id;
  }

  function openAsync<T>(renderer: OverlayAsyncRenderer<T>, options?: OverlayOpenOptions) {
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      open((props) => {
        const close = (param: T) => {
          if (settled) return;
          settled = true;
          resolve(param);
          props.close();
        };
        const rejectOverlay = (reason?: unknown) => {
          if (settled) return;
          settled = true;
          reject(reason);
          props.close();
        };
        return renderer({ ...props, close, reject: rejectOverlay });
      }, options);
    });
  }

  function initialize(id: string) {
    store.set(slotsAtom, (prev) => {
      if (!prev[id] || prev[id].isOpen) return prev;

      return {
        ...prev,
        [id]: {
          ...prev[id],
          isOpen: true,
        },
      };
    });
  }

  function close(overlayId: string) {
    closeById(overlayId);
  }

  function closeSlot(slot = 'modal') {
    const items = store.get(slotsAtom);
    Object.values(items)
      .filter((item) => item.slot === slot)
      .forEach((item) => {
        closeById(item.id);
      });
  }

  function closeAll() {
    Object.values(store.get(slotsAtom)).forEach((item) => {
      if (item.isOpen) closeById(item.id);
    });
  }

  function closeById(id: string) {
    if (id in closedTable) {
      closedTable[id].resolve();
      delete closedTable[id];
    }
    const item = store.get(slotsAtom)[id];
    if (!item) return;
    // Not initialized yet (isOpen still false). close() must drop the slot or
    // Strict Mode remount stacks a second modal on top of the leaked one.
    if (!item.isOpen) {
      unmount(id);
      return;
    }
    store.set(slotsAtom, (prev) => {
      if (!prev[id]?.isOpen) return prev;
      return {
        ...prev,
        [id]: {
          ...prev[id],
          isOpen: false,
        },
      };
    });
  }

  function unmount(id: string) {
    if (!store.get(slotsAtom)[id]) return;
    store.set(slotsAtom, (prev) => {
      if (!prev[id]) return prev;
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }

  function unmountAll() {
    Object.keys(store.get(slotsAtom)).forEach((id) => {
      unmount(id);
    });
  }

  function getClosed(id: string): Promise<void> {
    if (!closedTable[id]) return Promise.resolve();
    return closedTable[id];
  }

  return {
    presenceItemsAtom,
    open,
    openAsync,
    close,
    closeById,
    unmount,
    closeAll,
    unmountAll,
    closeSlot,
    initialize,
    getClosed,
  };
});
overlayBunja.debugLabel = 'overlayBunja';

interface OverlayItem {
  id: string;
  instanceKey: string;
  slot: string;
  renderer: OverlayRenderer;
  isOpen: boolean;
}

const OverlayItemContext = createContext<OverlayItem>(null as unknown as OverlayItem);
const OverlayItemScope = createScopeFromContext(OverlayItemContext);

export const overlayItemBunja = bunja(() => {
  const overlayItem = bunja.use(OverlayItemScope);
  const overlayBunjaInstance = bunja.use(overlayBunja);

  function close() {
    overlayBunjaInstance.closeById(overlayItem.id);
  }

  function unmount() {
    overlayBunjaInstance.unmount(overlayItem.id);
  }

  return {
    id: overlayItem.id,
    isOpen: overlayItem.isOpen,
    close,
    unmount,
  };
});
overlayItemBunja.debugLabel = 'overlayItemBunja';
